import { Link } from 'react-router-dom';
import { AlertTriangle, Briefcase, CalendarClock, FileWarning, Gavel, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import type { PipelineResumo } from '@/types/gestaoLicitacoes';

export interface DocumentoVencendo {
  id: number;
  categoria: string;
  nome_arquivo: string;
  data_validade: string | null;
}

interface ResumoGestaoFeedProps {
  empresaId: number;
  oportunidadesPendentes: number;
  resumo: PipelineResumo | null;
  documentosVencendo: DocumentoVencendo[];
  isLoading?: boolean;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function daysUntil(value: string | null) {
  if (!value) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Math.round((date.getTime() - today.getTime()) / 86400000);
}

function documentAlertText(documento: DocumentoVencendo) {
  const days = daysUntil(documento.data_validade);
  if (days === null) return documento.nome_arquivo;
  if (days < 0) return `${documento.nome_arquivo} venceu há ${Math.abs(days)} dia${Math.abs(days) === 1 ? '' : 's'}`;
  if (days === 0) return `${documento.nome_arquivo} vence hoje`;
  return `${documento.nome_arquivo} vence em ${days} dia${days === 1 ? '' : 's'}`;
}

export function ResumoGestaoFeed({
  empresaId,
  oportunidadesPendentes,
  resumo,
  documentosVencendo,
  isLoading,
}: ResumoGestaoFeedProps) {
  const emDisputa = resumo?.porEtapa.em_disputa ?? 0;
  const alertas = documentosVencendo.slice(0, 2);

  return (
    <section aria-labelledby="resumo-gestao-title" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 id="resumo-gestao-title" className="text-lg font-semibold text-ds-text-primary">Visão rápida da gestão</h2>
          <p className="text-sm text-ds-text-secondary">Prioridades da empresa para decidir e manter a documentação em dia.</p>
        </div>
        <Link to={`/gestao-licitacoes?empresa=${empresaId}`} className="inline-flex items-center gap-1 text-sm font-medium text-ds-brand hover:underline">
          Ver gestão <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Metric icon={CalendarClock} label="Para decidir" value={oportunidadesPendentes} detail="oportunidades compatíveis" loading={isLoading} />
        <Metric icon={Briefcase} label="Em gestão" value={resumo?.totalEmGestao ?? 0} detail={resumo ? `${formatCurrency(resumo.valorTotalEstimado)} estimados` : 'licitações no pipeline'} loading={isLoading} />
        <Metric icon={Gavel} label="Em disputa" value={emDisputa} detail="licitações em fase ativa" loading={isLoading} />
        <Metric icon={FileWarning} label="Documentos em atenção" value={documentosVencendo.length} detail="vencidos ou vencendo em 30 dias" loading={isLoading} tone={documentosVencendo.length > 0 ? 'warning' : 'default'} />
      </div>

      <Card className="p-4" aria-live="polite">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <div className={`mt-0.5 rounded-lg p-2 ${alertas.length ? 'bg-ds-warning/10 text-ds-warning' : 'bg-ds-brand-subtle/50 text-ds-brand'}`}>
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-ds-text-primary">Alertas da empresa</h3>
              {isLoading ? <div className="mt-2 h-4 w-64 animate-pulse rounded bg-ds-surface2" /> : alertas.length ? (
                <ul className="mt-1 space-y-1 text-sm text-ds-text-secondary">
                  {alertas.map((documento) => <li key={documento.id}>{documentAlertText(documento)}</li>)}
                </ul>
              ) : <p className="mt-1 text-sm text-ds-text-secondary">Nenhum documento vence nos próximos 30 dias.</p>}
            </div>
          </div>
          <Link to={`/empresas/${empresaId}/perfil-tecnico`} className="shrink-0 inline-flex items-center justify-center rounded-lg border border-ds-border bg-ds-surface px-3 py-2 text-sm font-medium text-ds-text-primary transition-colors hover:bg-ds-surface2">
            Conferir documentos
          </Link>
        </div>
      </Card>
    </section>
  );
}

function Metric({ icon: Icon, label, value, detail, loading, tone = 'default' }: {
  icon: typeof CalendarClock;
  label: string;
  value: number;
  detail: string;
  loading?: boolean;
  tone?: 'default' | 'warning';
}) {
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-ds-text-secondary">{label}</p>
          {loading ? <div className="mt-2 h-7 w-10 animate-pulse rounded bg-ds-surface2" /> : <p className="mt-1 text-2xl font-bold text-ds-text-primary">{value}</p>}
        </div>
        <div className={`rounded-lg p-2 ${tone === 'warning' ? 'bg-ds-warning/10 text-ds-warning' : 'bg-ds-brand-subtle/50 text-ds-brand'}`}><Icon className="h-4 w-4" /></div>
      </div>
      <p className="mt-2 text-xs text-ds-text-secondary">{detail}</p>
    </Card>
  );
}
